import {
  DocumentCategory,
  DocumentsConfig,
  SelectedThirdParty,
  VerificationDateConfig,
} from 'pages/initiate-due-diligence/types';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Stroke, LoadingContainer } from './MultipleDocumentUploadWrapper.styles';
import MultipleUploadOption from 'components/molecules/multiple-upload-option';
import DocumentUploadModal from '../document-upload-modal';
import {
  DocumentData,
  FileInformationResponse,
  fileInformationResponseToDocumentData,
  isIndividualRelatedEntityDocumentRequiredDependingOnAge,
} from 'helpers/documentHelper';
import DocumentSelectModal from '../document-select-modal';
import {
  hasValidVerificationDateConfig,
  isVerificationDateValid,
} from 'helpers/verificationDateHelper';
import { SelectableDocument } from '../document-select-modal/types';
import { FieldError } from 'react-hook-form';
import { ThirdPartyType } from 'types';
import { useFetch, useSignalR } from 'hooks';
import { useAuth } from 'auth';
import { useConfig } from 'config';
import { Loading } from 'components/atoms';
import DocumentBulkFileInput, {
  PendingDocumentUpload,
} from '../document-bulk-file-input/DocumentBulkFileInput';
import { useUserSettings } from 'context/DataContext';
import { ReactComponent as SystemSvg } from 'assets/svg/Icons/System.svg';

const LocalDeviceTriggerRow = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => `${theme.pxToRem(6)} ${theme.pxToRem(12)}`};

  &:hover {
    background-color: #f0f0f0;
  }

  .icon {
    margin-right: ${({ theme }) => theme.pxToRem(5)};
    width: ${({ theme }) => theme.pxToRem(22)};
    height: ${({ theme }) => theme.pxToRem(22)};
    flex-shrink: 0;
  }
`;

interface UploadModalState {
  isOpen: boolean;
  categoryId: number;
  maxUploads: number;
  verificationDateConfig: VerificationDateConfig | null;
  isOptional: boolean;
  isLinkedThirdPartyDocument: boolean;
}

interface SelectModalState {
  isOpen: boolean;
  categoryId: number;
  maxUploads: number;
  verificationDateConfig: VerificationDateConfig | null;
  isOptional: boolean;
  isLinkedThirdPartyDocument: boolean;
}

const defaultUploadModalState: UploadModalState = {
  isOpen: false,
  categoryId: undefined,
  maxUploads: undefined,
  verificationDateConfig: null,
  isOptional: false,
  isLinkedThirdPartyDocument: false,
};

const defaultSelectModalState: SelectModalState = {
  isOpen: false,
  categoryId: undefined,
  maxUploads: undefined,
  verificationDateConfig: null,
  isOptional: false,
  isLinkedThirdPartyDocument: false,
};

interface FileStatusSignalRMessage {
  id?: string;
  Id?: string;
  status?: string;
  Status?: string;
  errorMessage?: string | null;
  ErrorMessage?: string | null;
}

interface FileStatusUpdate {
  status: string;
  errorMessage?: string | null;
}

interface PendingDocumentUploadCard extends PendingDocumentUpload {
  categoryId?: number;
  entityId: string;
  isOptionalDocument: boolean;
  isLinkedThirdPartyDocument: boolean;
  fileStatus?: string | null;
}

const isBlockingDocumentStatus = (status?: string | null) => {
  const normalizedStatus = status?.toLowerCase().replace(/\s/g, '');

  return normalizedStatus === 'inprogress' || normalizedStatus === 'error';
};

interface MultipleUploadOptionsSectionTypes {
  mainThirdPartyId: string;
  thirdPartyType: ThirdPartyType;
  documentsConfig: DocumentsConfig;
  documents: DocumentData[];
  categories: DocumentCategory[];
  defaultOptionalCategoryId?: number;
  selectedRelatedThirdParties: SelectedThirdParty[];
  selectedThirdPartyId: string;
  isMainThirdPartySelected: boolean;
  onSuccessDocumentUpload?: (document: DocumentData) => void;
  onDeleteDocument?: (id: string, entityId: string, isOptional: boolean) => void;
  onAttachDocuments?: (documents: DocumentData[]) => void;
  onDocumentStatusChange?: (id: string, status: string, errorMessage?: string | null) => void;
  onPendingUploadsChange?: (pendingUploadsCount: number) => void;
  onBlockingUploadsCountChange?: (blockingUploadsCount: number) => void;
  showMandatoryDocuments?: boolean;
  error: FieldError;
}

const MultipleUploadOptionsSection: FC<MultipleUploadOptionsSectionTypes> = ({
  mainThirdPartyId,
  thirdPartyType,
  documentsConfig,
  documents,
  categories,
  defaultOptionalCategoryId,
  selectedRelatedThirdParties,
  selectedThirdPartyId,
  onSuccessDocumentUpload,
  onDeleteDocument,
  onAttachDocuments,
  onDocumentStatusChange,
  onPendingUploadsChange,
  onBlockingUploadsCountChange,
  showMandatoryDocuments = true,
  isMainThirdPartySelected,
  error,
}: MultipleUploadOptionsSectionTypes) => {
  const { tokenInfo } = useAuth();
  const { clientId, isEnhancedDocumentUploadControlsEnabled } = useUserSettings();
  const { vantageWebApi } = useConfig();
  const showBulkDocumentUpload = isEnhancedDocumentUploadControlsEnabled === false;
  const [documentUploadModalState, setDocumentUploadModalState] =
    useState<UploadModalState>(defaultUploadModalState);
  const [selectModalState, setSelectModalState] =
    useState<SelectModalState>(defaultUploadModalState);
  const [signalRMessage, setSignalRMessage] = useState<FileStatusSignalRMessage | null>(null);
  const [fileStatusUpdates, setFileStatusUpdates] = useState<Record<string, FileStatusUpdate>>({});
  const [pendingDocumentUploads, setPendingDocumentUploads] = useState<PendingDocumentUploadCard[]>(
    []
  );

  const { connectionId } = useSignalR(
    setSignalRMessage,
    'fileupload',
    clientId,
    showBulkDocumentUpload
  );

  const { data: filesData, loading: filesDataLoading } = useFetch<FileInformationResponse[]>(
    `${vantageWebApi}/thirdparty/${selectedThirdPartyId}/files`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokenInfo.accessToken}`,
      },
    }
  );

  const existingThirdPartyFiles = useMemo(() => {
    if (!filesData) {
      return [];
    }
    return filesData.map((fileInfo) => fileInformationResponseToDocumentData(fileInfo));
  }, [filesData]);

  const MAX_THIRD_PARTY_DOCUMENTS = 60;

  const [newDocumentUploadWarnings, setNewDocumentUploadWarnings] = useState<
    Record<string, string | undefined>
  >({});

  const getUploadControlKey = (
    categoryId: number | undefined,
    isOptional: boolean,
    isLinkedThirdPartyDocument: boolean
  ) => `${categoryId ?? 'optional'}-${isOptional}-${isLinkedThirdPartyDocument}`;

  const getRemainingThirdPartyWideUploads = () => {
    const documentsAddedThisSession =
      documents.filter((document) => document.entityId === selectedThirdPartyId).length +
      pendingDocumentUploads.filter(
        (pendingUpload) => pendingUpload.entityId === selectedThirdPartyId
      ).length;

    return Math.max(
      MAX_THIRD_PARTY_DOCUMENTS - existingThirdPartyFiles.length - documentsAddedThisSession,
      0
    );
  };

  const isRelatedThirdPartySelected = !isMainThirdPartySelected;

  const isValid = useCallback(
    (categoryId: number) => {
      if (error?.type?.includes(categoryId.toString())) {
        return false;
      }
      return true;
    },
    [error?.type]
  );

  const isOptionalValid = useMemo(() => {
    if (error?.type?.includes('optional')) {
      return false;
    }
    return true;
  }, [error?.type]);

  const selectedRelatedThirdParty = useMemo(() => {
    return selectedRelatedThirdParties.find((x) => x.id === selectedThirdPartyId);
  }, [selectedRelatedThirdParties, selectedThirdPartyId]);

  const selectedEntityType = isMainThirdPartySelected
    ? thirdPartyType
    : (selectedRelatedThirdParty?.type as ThirdPartyType);

  const blockingUploadsCount = useMemo(() => {
    const blockingUploadedDocumentsCount = documents.filter((document) =>
      isBlockingDocumentStatus(fileStatusUpdates[document.id]?.status ?? document.fileStatus)
    ).length;

    return pendingDocumentUploads.length + blockingUploadedDocumentsCount;
  }, [documents, fileStatusUpdates, pendingDocumentUploads]);

  useEffect(() => {
    onBlockingUploadsCountChange?.(blockingUploadsCount);
  }, [blockingUploadsCount, onBlockingUploadsCountChange]);

  useEffect(() => {
    return () => onBlockingUploadsCountChange?.(0);
  }, [onBlockingUploadsCountChange]);

  useEffect(() => {
    const documentId = signalRMessage?.id ?? signalRMessage?.Id;
    const status = signalRMessage?.status ?? signalRMessage?.Status;
    const errorMessage = signalRMessage?.errorMessage ?? signalRMessage?.ErrorMessage;

    if (!documentId || !status) {
      return;
    }

    setFileStatusUpdates((currentUpdates) => ({
      ...currentUpdates,
      [documentId]: {
        status,
        errorMessage,
      },
    }));

    onDocumentStatusChange?.(documentId, status, errorMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signalRMessage]);

  const thirdPartyConfiguredDocuments = useMemo(() => {
    if (thirdPartyType === ThirdPartyType.Business) {
      return documentsConfig.thirdPartyDocuments.thirdPartyConfiguredDocumentsBusiness;
    } else {
      return documentsConfig.thirdPartyDocuments.thirdPartyConfiguredDocumentsIndividual;
    }
  }, [thirdPartyType, documentsConfig.thirdPartyDocuments]);

  const relatedEntityConfiguredDocuments = useMemo(() => {
    if (selectedRelatedThirdParty?.type === ThirdPartyType.Business) {
      return documentsConfig.relatedThirdPartiesDocuments
        .linkedThirdPartyConfiguredDocumentsBusiness;
    } else {
      return documentsConfig.relatedThirdPartiesDocuments
        .linkedThirdPartyConfiguredDocumentsIndividual;
    }
  }, [selectedRelatedThirdParty?.type, documentsConfig.relatedThirdPartiesDocuments]);

  const getLabelByCategoryId = (categories: DocumentCategory[], categoryId: number) => {
    const categoryRecord = categories.find((category) => category.id === categoryId);

    return categoryRecord.category;
  };

  const handleNewDocumentUploadForRequiredDocuments = (
    categoryId: number,
    maxUploads: number,
    verificationDateConfig: VerificationDateConfig,
    isLinkedThirdPartyDocument: boolean
  ) => {
    setDocumentUploadModalState({
      isOpen: true,
      categoryId: categoryId,
      maxUploads,
      verificationDateConfig,
      isOptional: false,
      isLinkedThirdPartyDocument,
    });
  };

  const handleBulkDocumentUpload = (
    fileInformation: FileInformationResponse,
    entityId: string,
    isRelatedEntityDocument: boolean,
    isOptional: boolean,
    pendingUpload?: PendingDocumentUpload
  ) => {
    if (pendingUpload) {
      removePendingDocumentUpload(pendingUpload.uploadId);
    }

    handleSuccessDocumentUpload(
      {
        ...fileInformation,
        fileStatus: fileInformation.fileStatus ?? 'In Progress',
      },
      entityId,
      isRelatedEntityDocument,
      isOptional
    );
  };

  const removePendingDocumentUpload = useCallback((uploadId: string) => {
    setPendingDocumentUploads((currentUploads) =>
      currentUploads.filter((pendingUpload) => pendingUpload.uploadId !== uploadId)
    );
  }, []);

  const markCompletedPendingDocumentUpload = useCallback((uploadId: string) => {
    setPendingDocumentUploads((currentUploads) =>
      currentUploads.map((pendingUpload) =>
        pendingUpload.uploadId === uploadId && !pendingUpload.failed
          ? { ...pendingUpload, fileStatus: pendingUpload.fileStatus ?? 'In Progress' }
          : pendingUpload
      )
    );
  }, []);

  const filterForRequiredPendingDocumentCardsList = (
    pendingUploads: PendingDocumentUploadCard[],
    entityId: string,
    categoryId: number,
    isRelatedEntityDocument: boolean
  ) => {
    return pendingUploads.filter(
      (pendingUpload) =>
        pendingUpload.categoryId === categoryId &&
        pendingUpload.entityId === entityId &&
        pendingUpload.isLinkedThirdPartyDocument === isRelatedEntityDocument &&
        !pendingUpload.isOptionalDocument
    );
  };

  const filterForOptionalPendingDocumentCardsList = (
    pendingUploads: PendingDocumentUploadCard[]
  ) => {
    return pendingUploads.filter(
      (pendingUpload) =>
        pendingUpload.isOptionalDocument && !pendingUpload.isLinkedThirdPartyDocument
    );
  };

  const renderNewDocumentUploadControl = (
    documentsCount: number,
    maxUploads: number,
    categoryId: number | undefined,
    isOptional: boolean,
    isLinkedThirdPartyDocument: boolean
  ) => {
    if (!showBulkDocumentUpload) {
      return null;
    }

    const effectiveMaxUploads = Math.min(
      maxUploads,
      documentsCount + getRemainingThirdPartyWideUploads()
    );
    const controlKey = getUploadControlKey(categoryId, isOptional, isLinkedThirdPartyDocument);
    const thirdPartyCapMessage =
      effectiveMaxUploads < maxUploads
        ? `This third party has reached the maximum of ${MAX_THIRD_PARTY_DOCUMENTS} documents allowed.`
        : undefined;

    return (closeMenu: () => void) => (
      <LocalDeviceTriggerRow data-testid="NewDocumentsOption">
        <SystemSvg className="icon" />
        <DocumentBulkFileInput
          documentsCount={documentsCount}
          entityType={selectedEntityType}
          buttonText="Your local device"
          buttonIcon={null}
          showLabel={false}
          outlineButton={false}
          textButton
          smallButton={false}
          plainMenuText
          maxDocuments={effectiveMaxUploads}
          maxFilesPerUpload={effectiveMaxUploads}
          maxFilesPerUploadExceededMessage={thirdPartyCapMessage}
          maxDocumentsReachedMessage={thirdPartyCapMessage}
          hideValidationWarning
          onValidationError={(message) =>
            setNewDocumentUploadWarnings((currentWarnings) => ({
              ...currentWarnings,
              [controlKey]: message,
            }))
          }
          categoryId={categoryId ?? defaultOptionalCategoryId}
          thirdPartyId={selectedThirdPartyId}
          signalRConnectionId={connectionId}
          linkToThirdParty={true}
          isBulkUpload
          onFilesSelected={closeMenu}
          showValidationErrorsAsPendingCards
          onPendingFileUploadsStart={(pendingUploads) =>
            setPendingDocumentUploads((currentUploads) => [
              ...currentUploads,
              ...pendingUploads.map((pendingUpload) => ({
                ...pendingUpload,
                categoryId: categoryId ?? defaultOptionalCategoryId,
                entityId: selectedThirdPartyId,
                isOptionalDocument: isOptional,
                isLinkedThirdPartyDocument,
              })),
            ])
          }
          onPendingFileUploadError={(uploadId, errorMessage) =>
            setPendingDocumentUploads((currentUploads) =>
              currentUploads.map((pendingUpload) =>
                pendingUpload.uploadId === uploadId
                  ? { ...pendingUpload, failed: true, errorMessage }
                  : pendingUpload
              )
            )
          }
          onPendingFileUploadComplete={markCompletedPendingDocumentUpload}
          onPendingUploadsChange={onPendingUploadsChange}
          onSuccessDocumentUpload={(fileInformation, pendingUpload) => {
            handleBulkDocumentUpload(
              fileInformation,
              selectedThirdPartyId,
              isLinkedThirdPartyDocument,
              isOptional,
              pendingUpload
            );
          }}
        />
      </LocalDeviceTriggerRow>
    );
  };

  const handleNewDocumentUploadForOptionalDocuments = (maxUploads: number) => {
    setDocumentUploadModalState({
      isOpen: true,
      categoryId: undefined,
      maxUploads,
      verificationDateConfig: null,
      isOptional: true,
      isLinkedThirdPartyDocument: false,
    });
  };

  const handleSelectModalForRequiredDocuments = (
    categoryId: number,
    maxUploads: number,
    verificationDateConfig: VerificationDateConfig,
    isLinkedThirdPartyDocument: boolean
  ) => {
    setSelectModalState({
      isOpen: true,
      categoryId,
      maxUploads,
      verificationDateConfig,
      isOptional: false,
      isLinkedThirdPartyDocument,
    });
  };

  const handleSelectModalForOptionalDocuments = (maxUploads: number) => {
    setSelectModalState({
      isOpen: true,
      categoryId: undefined,
      maxUploads,
      verificationDateConfig: null,
      isOptional: true,
      isLinkedThirdPartyDocument: false,
    });
  };

  const filterForRequiredDocumentCardsList = (
    documents: DocumentData[],
    entityId: string,
    categoryId: number,
    isRelatedEntityDocument: boolean
  ) => {
    return documents.filter(
      (doc) =>
        doc.categoryId === categoryId &&
        doc.entityId === entityId &&
        doc.isLinkedThirdPartyDocument === isRelatedEntityDocument &&
        !doc.isOptionalDocument
    );
  };

  const filterForOptionalDocumentCardsList = (documents: DocumentData[]) => {
    return documents.filter((doc) => doc.isOptionalDocument && !doc.isLinkedThirdPartyDocument);
  };

  const getRequiredDocumentsCount = (
    entityId: string,
    categoryId: number,
    isRelatedEntityDocument: boolean
  ) => {
    return (
      filterForRequiredDocumentCardsList(documents, entityId, categoryId, isRelatedEntityDocument)
        .length +
      filterForRequiredPendingDocumentCardsList(
        pendingDocumentUploads,
        entityId,
        categoryId,
        isRelatedEntityDocument
      ).length
    );
  };

  const getOptionalDocumentsCount = () => {
    return (
      filterForOptionalDocumentCardsList(documents).length +
      filterForOptionalPendingDocumentCardsList(pendingDocumentUploads).length
    );
  };

  const filterSelectableFiles = (
    thirdPartyFiles: DocumentData[],
    categoryId: number,
    verificationDateConfig: VerificationDateConfig,
    isOptional: boolean
  ): SelectableDocument[] => {
    if (isOptional) {
      return thirdPartyFiles.map((tFile) => ({
        ...tFile,
        isOptionalDocument: true,
        selected: false,
      }));
    }

    return thirdPartyFiles
      .filter((tFile) => tFile.categoryId === categoryId)
      .filter((tFile) => {
        if (!verificationDateConfig) {
          return true;
        }

        if (!hasValidVerificationDateConfig(verificationDateConfig)) {
          return true;
        }

        if (!tFile.verificationDate) {
          return false;
        }

        return isVerificationDateValid(verificationDateConfig, tFile.verificationDate);
      })
      .map((tFile) => ({
        ...tFile,
        isOptionalDocument: false,
        selected: false,
      }));
  };

  const getRemainingUploads = (
    categoryMaxUploads: number,
    selectModalState: SelectModalState,
    isOptional: boolean
  ) => {
    if (isOptional) {
      return categoryMaxUploads - getOptionalDocumentsCount();
    }

    return (
      categoryMaxUploads -
      getRequiredDocumentsCount(
        selectedThirdPartyId,
        selectModalState.categoryId,
        selectModalState.isLinkedThirdPartyDocument
      )
    );
  };

  const handleSuccessDocumentUpload = (
    fileInformation: FileInformationResponse,
    entityId: string,
    isRelatedEntityDocument: boolean,
    isOptional: boolean
  ) => {
    const documentData = fileInformationResponseToDocumentData(
      fileInformation,
      isRelatedEntityDocument,
      entityId,
      isOptional
    );
    onSuccessDocumentUpload(documentData);
  };

  const handleAttachDocuments = (
    documents: SelectableDocument[],
    entityId: string,
    isLinkedThirdPartyDocument: boolean
  ) => {
    onAttachDocuments(documents.map((doc) => ({ ...doc, entityId, isLinkedThirdPartyDocument })));
  };

  if (filesDataLoading) {
    return (
      <LoadingContainer>
        <Loading />
      </LoadingContainer>
    );
  }

  return (
    <>
      {isMainThirdPartySelected && (
        <>
          {showMandatoryDocuments && documentsConfig.isRequiredDocumentsForThirdParty && (
            <>
              {thirdPartyConfiguredDocuments.map((configuredDocument) => {
                return (
                  <MultipleUploadOption
                    key={configuredDocument.categoryId}
                    label={getLabelByCategoryId(categories, configuredDocument.categoryId)}
                    required={true}
                    showSidebar={true}
                    maxUploads={configuredDocument.maxUploads}
                    valid={isValid(configuredDocument.categoryId)}
                    documents={filterForRequiredDocumentCardsList(
                      documents,
                      selectedThirdPartyId,
                      configuredDocument.categoryId,
                      false
                    )}
                    pendingDocuments={filterForRequiredPendingDocumentCardsList(
                      pendingDocumentUploads,
                      selectedThirdPartyId,
                      configuredDocument.categoryId,
                      false
                    )}
                    fileStatusUpdates={fileStatusUpdates}
                    onExistingDocumentUpload={() =>
                      handleSelectModalForRequiredDocuments(
                        configuredDocument.categoryId,
                        configuredDocument.maxUploads,
                        configuredDocument.verificationDateConfig,
                        false
                      )
                    }
                    onNewDocumentUpload={() => {
                      handleNewDocumentUploadForRequiredDocuments(
                        configuredDocument.categoryId,
                        configuredDocument.maxUploads,
                        configuredDocument.verificationDateConfig,
                        false
                      );
                    }}
                    onRemoveDocument={(id) => onDeleteDocument(id, selectedThirdPartyId, false)}
                    onRemovePendingDocument={removePendingDocumentUpload}
                    showExistingDocumentsOption={
                      filterSelectableFiles(
                        existingThirdPartyFiles,
                        configuredDocument.categoryId,
                        configuredDocument.verificationDateConfig,
                        false
                      ).length > 0
                    }
                    newDocumentUploadControl={renderNewDocumentUploadControl(
                      getRequiredDocumentsCount(
                        selectedThirdPartyId,
                        configuredDocument.categoryId,
                        false
                      ),
                      configuredDocument.maxUploads,
                      configuredDocument.categoryId,
                      false,
                      false
                    )}
                    newDocumentUploadWarning={
                      newDocumentUploadWarnings[
                        getUploadControlKey(configuredDocument.categoryId, false, false)
                      ]
                    }
                  />
                );
              })}
            </>
          )}
          {documentsConfig.isOptionalDocumentsForThirdParty && (
            <>
              <Stroke />
              <MultipleUploadOption
                key="optional"
                label="Optional documents"
                required={false}
                showSidebar={true}
                maxUploads={documentsConfig.maxOptionalDocuments}
                valid={isOptionalValid}
                documents={filterForOptionalDocumentCardsList(documents)}
                pendingDocuments={filterForOptionalPendingDocumentCardsList(pendingDocumentUploads)}
                fileStatusUpdates={fileStatusUpdates}
                onExistingDocumentUpload={() =>
                  handleSelectModalForOptionalDocuments(documentsConfig.maxOptionalDocuments)
                }
                onNewDocumentUpload={() =>
                  handleNewDocumentUploadForOptionalDocuments(documentsConfig.maxOptionalDocuments)
                }
                onRemoveDocument={(id) => onDeleteDocument(id, selectedThirdPartyId, true)}
                onRemovePendingDocument={removePendingDocumentUpload}
                showExistingDocumentsOption={
                  filterSelectableFiles(existingThirdPartyFiles, undefined, null, true).length > 0
                }
                newDocumentUploadControl={renderNewDocumentUploadControl(
                  getOptionalDocumentsCount(),
                  documentsConfig.maxOptionalDocuments,
                  undefined,
                  true,
                  false
                )}
                newDocumentUploadWarning={
                  newDocumentUploadWarnings[getUploadControlKey(undefined, true, false)]
                }
              />
            </>
          )}
        </>
      )}
      {isRelatedThirdPartySelected && (
        <>
          {showMandatoryDocuments && documentsConfig.isRequiredDocumentsForRelatedThirdParties && (
            <>
              {relatedEntityConfiguredDocuments.map((configuredDocument) => {
                return (
                  <MultipleUploadOption
                    key={configuredDocument.categoryId}
                    label={getLabelByCategoryId(categories, configuredDocument.categoryId)}
                    required={isIndividualRelatedEntityDocumentRequiredDependingOnAge(
                      selectedRelatedThirdParty,
                      configuredDocument
                    )}
                    showSidebar={true}
                    maxUploads={configuredDocument.maxUploads}
                    valid={isValid(configuredDocument.categoryId)}
                    documents={filterForRequiredDocumentCardsList(
                      documents,
                      selectedThirdPartyId,
                      configuredDocument.categoryId,
                      true
                    )}
                    pendingDocuments={filterForRequiredPendingDocumentCardsList(
                      pendingDocumentUploads,
                      selectedThirdPartyId,
                      configuredDocument.categoryId,
                      true
                    )}
                    fileStatusUpdates={fileStatusUpdates}
                    onExistingDocumentUpload={() =>
                      handleSelectModalForRequiredDocuments(
                        configuredDocument.categoryId,
                        configuredDocument.maxUploads,
                        configuredDocument.verificationDateConfig,
                        true
                      )
                    }
                    onNewDocumentUpload={() => {
                      handleNewDocumentUploadForRequiredDocuments(
                        configuredDocument.categoryId,
                        configuredDocument.maxUploads,
                        configuredDocument.verificationDateConfig,
                        true
                      );
                    }}
                    onRemoveDocument={(id) => onDeleteDocument(id, selectedThirdPartyId, false)}
                    onRemovePendingDocument={removePendingDocumentUpload}
                    showExistingDocumentsOption={
                      filterSelectableFiles(
                        existingThirdPartyFiles,
                        configuredDocument.categoryId,
                        configuredDocument.verificationDateConfig,
                        false
                      ).length > 0
                    }
                    newDocumentUploadControl={renderNewDocumentUploadControl(
                      getRequiredDocumentsCount(
                        selectedThirdPartyId,
                        configuredDocument.categoryId,
                        true
                      ),
                      configuredDocument.maxUploads,
                      configuredDocument.categoryId,
                      false,
                      true
                    )}
                    newDocumentUploadWarning={
                      newDocumentUploadWarnings[
                        getUploadControlKey(configuredDocument.categoryId, false, true)
                      ]
                    }
                  />
                );
              })}
            </>
          )}
        </>
      )}
      {documentUploadModalState.isOpen && (
        <DocumentUploadModal
          thirdPartyId={selectedThirdPartyId}
          categoryId={documentUploadModalState.categoryId}
          entityType={selectedEntityType}
          isOpen={true}
          onClose={() => {
            setDocumentUploadModalState(defaultUploadModalState);
          }}
          onSuccessDocumentUpload={(fileInfo) =>
            handleSuccessDocumentUpload(
              fileInfo,
              selectedThirdPartyId,
              documentUploadModalState.isLinkedThirdPartyDocument,
              documentUploadModalState.isOptional
            )
          }
          verificationDateConfig={documentUploadModalState.verificationDateConfig}
        />
      )}
      {selectModalState.isOpen && (
        <DocumentSelectModal
          thirdPartyId={mainThirdPartyId}
          maxUploads={getRemainingUploads(
            selectModalState.maxUploads,
            selectModalState,
            selectModalState.isOptional
          )}
          isOpen={true}
          selectableDocuments={filterSelectableFiles(
            existingThirdPartyFiles,
            selectModalState.categoryId,
            selectModalState.verificationDateConfig,
            selectModalState.isOptional
          )}
          onClose={() => setSelectModalState(defaultSelectModalState)}
          onAttachDocuments={(documents) => {
            handleAttachDocuments(
              documents,
              selectedThirdPartyId,
              selectModalState.isLinkedThirdPartyDocument
            );
          }}
        />
      )}
    </>
  );
};

export default MultipleUploadOptionsSection;
